import { useEffect, useRef, useState } from "react";
import {
  SessionResult,
  fetchCatalogExercises,
  startedAtMsOf,
  type TimedSessionResult,
} from "@jackedlogs/shared";

import type {
  ProgressApiFetch,
  ProgressDeps,
  SessionResultsCacheModule,
  SessionResultsSnapshot,
} from "./types";

type ListResp = { items?: unknown[] };

export type UseSessionResultsOptions = {
  apiFetch: ProgressApiFetch;
  // Shared single-flight instance (host-owned, so the save path
  // invalidates the same one the panels subscribe to).
  cache: SessionResultsCacheModule;
  // Optional cold-boot disk read (mobile AsyncStorage, web localStorage).
  readStale?: () => Promise<SessionResultsSnapshot | null>;
  // Optional best-effort persist of parsed results for the next cold boot.
  persistResults?: (results: TimedSessionResult[]) => void;
};

// Shared data hook for the Progress panels. ONE /api/session-results page +
// the catalog, parsed ONCE and shared across every consumer via the
// injected single-flight cache. Mirrors the mobile hook; the AsyncStorage
// SWR is now an injected concern (readStale / persistResults) so the hook
// stays platform-agnostic.
export function useSessionResults(opts: UseSessionResultsOptions): {
  results: TimedSessionResult[] | null;
  catalog: ReadonlyArray<import("@jackedlogs/shared").Exercise> | null;
  error: string | null;
} {
  const { cache } = opts;
  // Refs so a host passing fresh literals per render can't re-trigger the
  // mount effect (the unstable-object-dep lesson).
  const apiRef = useRef(opts.apiFetch);
  const staleRef = useRef(opts.readStale);
  const persistRef = useRef(opts.persistResults);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    staleRef.current = opts.readStale;
    persistRef.current = opts.persistResults;
  });

  const [snap, setSnap] = useState<SessionResultsSnapshot | null>(
    cache.getCached(),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSnapshot = async (): Promise<SessionResultsSnapshot> => {
      const api = apiRef.current;
      const [listResp, catalog] = await Promise.all([
        api<ListResp>("/api/session-results?limit=200"),
        fetchCatalogExercises(api),
      ]);
      const results: TimedSessionResult[] = [];
      for (const raw of listResp.items ?? []) {
        const p = SessionResult.safeParse(raw);
        if (!p.success) continue;
        results.push({ result: p.data, startedAtMs: startedAtMsOf(raw, p.data) });
      }
      persistRef.current?.(results);
      return { results, catalog };
    };

    const refresh = () => {
      cache
        .ensure(fetchSnapshot)
        .then((s) => {
          if (!cancelled) {
            setSnap(s);
            setError(null);
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setError(e instanceof Error ? e.message : "load_failed");
          }
        });
    };

    // Cold boot (no warm in-memory cache): paint the last persisted
    // snapshot immediately so the panels skip the network-length spinner.
    // Never overwrites a fresher snapshot the refresh already delivered.
    if (!cache.getCached() && staleRef.current) {
      void staleRef.current().then((stale) => {
        if (cancelled || stale === null) return;
        setSnap((cur) => cur ?? stale);
      });
    }
    const unsubscribe = cache.subscribe(() => {
      if (cancelled) return;
      setSnap(null);
      refresh();
    });
    refresh();
    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  return {
    results: snap?.results ?? null,
    catalog: snap?.catalog ?? null,
    error,
  };
}

// Convenience wrapper — every Tier-1 panel shares one set of ProgressDeps,
// so they all call this instead of re-spreading the same four fields.
export function useSessionResultsFromDeps(deps: ProgressDeps) {
  return useSessionResults({
    apiFetch: deps.apiFetch,
    cache: deps.resultsCache,
    readStale: deps.readStaleResults,
    persistResults: deps.persistResults,
  });
}
